import { KaijuId, TriageTag, KAIJU, TAG_TEMPLATES, FIRST_NAMES, LAST_NAMES } from './gameData';
import type { PatientTemplate } from './gameData';
import { loadJSON, saveLocal, saveToDb, initCache, clearCache } from './settingsCache';

const KEYS = {
  templates: 'mci-admin-templates',
  injuries: 'mci-admin-injuries',
  waveMessages: 'mci-admin-wave-messages',
  firstNames: 'mci-admin-first-names',
  lastNames: 'mci-admin-last-names',
  timers: 'mci-admin-timers',
  waves: 'mci-admin-waves',
  kaijuNames: 'mci-admin-kaiju-names',
  kaijuDifficulty: 'mci-admin-kaiju-difficulty',
  kaijuWaves: 'mci-admin-kaiju-waves',
  kaijuImages: 'mci-admin-kaiju-images',
  kaijuStoryImages: 'mci-admin-kaiju-story-images',
  introSlideTexts: 'mci-admin-intro-slide-texts',
  introSlideImages: 'mci-admin-intro-slide-images',
  introInstructions: 'mci-admin-intro-instructions',
  introMusic: 'mci-admin-intro-music',
  introTexts: 'mci-admin-intro-texts',
  algoImages: 'mci-admin-algo-images',
  pedsPercent: 'mci-admin-peds-percent',
  sharedInjuries: 'mci-admin-shared-injuries',
  rankings: 'mci-admin-rankings',
  outroImages: 'mci-admin-outro-images',
  outroMusic: 'mci-admin-outro-music',
  outroSlideTexts: 'mci-admin-outro-slide-texts',
};

// ── Init (delegates to shared cache — now synchronous) ─────

export function initGameSettings(): void {
  initCache();
}

// ── Save helper (writes to both localStorage + DB) ────────

async function saveSetting(key: string, data: unknown): Promise<void> {
  saveLocal(key, data);
  await saveToDb(key, data);
}

// ── Templates ──────────────────────────────────────────────

export function getTemplates(): Record<TriageTag, PatientTemplate[]> {
  return loadJSON(KEYS.templates) ?? TAG_TEMPLATES;
}

export async function saveTemplates(templates: Record<TriageTag, PatientTemplate[]>): Promise<void> {
  await saveSetting(KEYS.templates, templates);
}

// ── Injury types per kaiju ─────────────────────────────────

export interface InjuryWithTags {
  text: string;
  tags: TriageTag[];
}

/** Normalize old string[] format to enriched { text, tags }[] */
function normalizeInjuries(raw: unknown): InjuryWithTags[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.map(item => {
    if (typeof item === 'string') return { text: item, tags: [] as TriageTag[] };
    if (item && typeof item === 'object' && 'text' in item) return { text: item.text, tags: item.tags ?? [] };
    return { text: String(item), tags: [] as TriageTag[] };
  });
}

/** Returns plain string[] for backward compat */
export function getInjuryTypes(kaijuId: KaijuId): string[] {
  const overrides = loadJSON<Record<string, unknown>>(KEYS.injuries);
  const raw = overrides?.[kaijuId];
  if (!raw) return KAIJU[kaijuId].injuryTypes;
  const normalized = normalizeInjuries(raw);
  return normalized ? normalized.map(i => i.text) : KAIJU[kaijuId].injuryTypes;
}

/** Returns enriched format with tags */
export function getInjuryTypesWithTags(kaijuId: KaijuId): InjuryWithTags[] {
  const overrides = loadJSON<Record<string, unknown>>(KEYS.injuries);
  const raw = overrides?.[kaijuId];
  if (!raw) return KAIJU[kaijuId].injuryTypes.map(text => ({ text, tags: [] }));
  return normalizeInjuries(raw) ?? KAIJU[kaijuId].injuryTypes.map(text => ({ text, tags: [] }));
}

export function getAllInjuryOverrides(): Record<string, InjuryWithTags[]> | null {
  const raw = loadJSON<Record<string, unknown>>(KEYS.injuries);
  if (!raw) return null;
  const result: Record<string, InjuryWithTags[]> = {};
  for (const [k, v] of Object.entries(raw)) {
    result[k] = normalizeInjuries(v) ?? [];
  }
  return result;
}

export async function saveInjuryTypes(kaijuId: KaijuId, injuries: InjuryWithTags[]): Promise<void> {
  const current = loadJSON<Record<string, unknown>>(KEYS.injuries) ?? {};
  current[kaijuId] = injuries;
  await saveSetting(KEYS.injuries, current);
}

// ── Shared Injuries (used by all kaiju) ────────────────────

export function getSharedInjuries(): InjuryWithTags[] {
  return loadJSON<InjuryWithTags[]>(KEYS.sharedInjuries) ?? [];
}

export async function saveSharedInjuries(injuries: InjuryWithTags[]): Promise<void> {
  await saveSetting(KEYS.sharedInjuries, injuries);
}


export function getWaveMessages(kaijuId: KaijuId): string[] {
  const overrides = loadJSON<Record<string, string[]>>(KEYS.waveMessages);
  return overrides?.[kaijuId] ?? KAIJU[kaijuId].waveMessages;
}

export function getAllWaveMessageOverrides(): Record<string, string[]> | null {
  return loadJSON(KEYS.waveMessages);
}

export async function saveWaveMessages(kaijuId: KaijuId, messages: string[]): Promise<void> {
  const current = loadJSON<Record<string, string[]>>(KEYS.waveMessages) ?? {};
  current[kaijuId] = messages;
  await saveSetting(KEYS.waveMessages, current);
}

// ── Names ──────────────────────────────────────────────────

export function getFirstNames(): string[] {
  return loadJSON(KEYS.firstNames) ?? FIRST_NAMES;
}

export function getLastNames(): string[] {
  return loadJSON(KEYS.lastNames) ?? LAST_NAMES;
}

export async function saveFirstNames(names: string[]): Promise<void> {
  await saveSetting(KEYS.firstNames, names);
}

export async function saveLastNames(names: string[]): Promise<void> {
  await saveSetting(KEYS.lastNames, names);
}

// ── Timers ─────────────────────────────────────────────────

export const DEFAULT_TIMERS: Record<TriageTag, number> = {
  red: 22,
  yellow: 40,
  green: 55,
  black: 30,
};

export function getTimers(): Record<TriageTag, number> {
  return loadJSON(KEYS.timers) ?? DEFAULT_TIMERS;
}

export async function saveTimers(timers: Record<TriageTag, number>): Promise<void> {
  await saveSetting(KEYS.timers, timers);
}

// ── Waves ──────────────────────────────────────────────────


// ── Kaiju Names & Descriptions ─────────────────────────────

export function getKaijuName(kaijuId: KaijuId): { name: string; description: string } {
  const overrides = loadJSON<Record<string, { name: string; description: string }>>(KEYS.kaijuNames);
  return overrides?.[kaijuId] ?? { name: KAIJU[kaijuId].name, description: KAIJU[kaijuId].description };
}

export function getAllKaijuNameOverrides(): Record<string, { name: string; description: string }> | null {
  return loadJSON(KEYS.kaijuNames);
}

export async function saveKaijuName(kaijuId: KaijuId, data: { name: string; description: string }): Promise<void> {
  const current = loadJSON<Record<string, { name: string; description: string }>>(KEYS.kaijuNames) ?? {};
  current[kaijuId] = data;
  await saveSetting(KEYS.kaijuNames, current);
}

// ── Kaiju Difficulty Labels ────────────────────────────────

export function getKaijuDifficulty(kaijuId: KaijuId): string {
  const overrides = loadJSON<Record<string, string>>(KEYS.kaijuDifficulty);
  return overrides?.[kaijuId] ?? '';
}

export function getAllKaijuDifficultyOverrides(): Record<string, string> | null {
  return loadJSON(KEYS.kaijuDifficulty);
}

export async function saveKaijuDifficulty(kaijuId: KaijuId, difficulty: string): Promise<void> {
  const current = loadJSON<Record<string, string>>(KEYS.kaijuDifficulty) ?? {};
  current[kaijuId] = difficulty;
  await saveSetting(KEYS.kaijuDifficulty, current);
}

// ── Per-Kaiju Waves ────────────────────────────────────────

export function getKaijuWaves(kaijuId: KaijuId): number[] | null {
  const overrides = loadJSON<Record<string, number[]>>(KEYS.kaijuWaves);
  return overrides?.[kaijuId] ?? null;
}

export async function saveKaijuWaves(kaijuId: KaijuId, waves: number[]): Promise<void> {
  const current = loadJSON<Record<string, number[]>>(KEYS.kaijuWaves) ?? {};
  current[kaijuId] = waves;
  await saveSetting(KEYS.kaijuWaves, current);
}

// ── Kaiju Images ───────────────────────────────────────────

export function getKaijuImage(kaijuId: KaijuId): string | null {
  const overrides = loadJSON<Record<string, string>>(KEYS.kaijuImages);
  return overrides?.[kaijuId] ?? null;
}

export function getAllKaijuImageOverrides(): Record<string, string> | null {
  return loadJSON(KEYS.kaijuImages);
}

export async function saveKaijuImage(kaijuId: KaijuId, url: string | null): Promise<void> {
  const current = loadJSON<Record<string, string>>(KEYS.kaijuImages) ?? {};
  if (url) {
    current[kaijuId] = url;
  } else {
    delete current[kaijuId];
  }
  await saveSetting(KEYS.kaijuImages, current);
}

// ── Intro Story ────────────────────────────────────────────

export function getIntroSlideTexts(): string[] {
  return loadJSON(KEYS.introSlideTexts) ?? ['', '', ''];
}

export async function saveIntroSlideTexts(texts: string[]): Promise<void> {
  await saveSetting(KEYS.introSlideTexts, texts);
}

export function getIntroSlideImage(slot: 2 | 3): string | null {
  const overrides = loadJSON<Record<string, string>>(KEYS.introSlideImages);
  return overrides?.[`slide${slot}`] ?? null;
}

export async function saveIntroSlideImage(slot: 2 | 3, url: string | null): Promise<void> {
  const current = loadJSON<Record<string, string>>(KEYS.introSlideImages) ?? {};
  if (url) {
    current[`slide${slot}`] = url;
  } else {
    delete current[`slide${slot}`];
  }
  await saveSetting(KEYS.introSlideImages, current);
}

export function getIntroInstructions(): string {
  return loadJSON(KEYS.introInstructions) ?? '';
}

export async function saveIntroInstructions(text: string): Promise<void> {
  await saveSetting(KEYS.introInstructions, text);
}

// ── Intro Texts (teaser + bridge) ──────────────────────────

const DEFAULT_INTRO_TEXTS = { teaser: 'The Year is 2035', bridge: "....but that's not you" };

export function getIntroTexts(): { teaser: string; bridge: string } {
  return loadJSON(KEYS.introTexts) ?? DEFAULT_INTRO_TEXTS;
}

export async function saveIntroTexts(texts: { teaser: string; bridge: string }): Promise<void> {
  await saveSetting(KEYS.introTexts, texts);
}

// ── Intro Music ────────────────────────────────────────────

export function getIntroMusic(): string | null {
  return loadJSON(KEYS.introMusic) ?? null;
}

export async function saveIntroMusic(url: string | null): Promise<void> {
  await saveSetting(KEYS.introMusic, url);
}

export function getAlgoImage(algo: 'start' | 'salt' | 'jumpstart'): string | null {
  const overrides = loadJSON<Record<string, string>>(KEYS.algoImages);
  return overrides?.[algo] ?? null;
}

export async function saveAlgoImage(algo: 'start' | 'salt' | 'jumpstart', url: string | null): Promise<void> {
  const current = loadJSON<Record<string, string>>(KEYS.algoImages) ?? {};
  if (url) {
    current[algo] = url;
  } else {
    delete current[algo];
  }
  await saveSetting(KEYS.algoImages, current);
}

// ── Pediatric Percent per Wave ─────────────────────────────

const DEFAULT_PEDS_PERCENT: Record<number, number> = {
  1: 0.10, 2: 0.15, 3: 0.20, 4: 0.25, 5: 0.25, 6: 0.30,
};

export function getPedsPercent(kaijuId?: KaijuId): Record<number, number> {
  const all = loadJSON<Record<string, Record<number, number>>>(KEYS.pedsPercent);
  if (kaijuId && all?.[kaijuId]) return all[kaijuId];
  return DEFAULT_PEDS_PERCENT;
}

export { DEFAULT_PEDS_PERCENT };

export async function savePedsPercent(kaijuId: KaijuId, data: Record<number, number>): Promise<void> {
  const current = loadJSON<Record<string, Record<number, number>>>(KEYS.pedsPercent) ?? {};
  current[kaijuId] = data;
  await saveSetting(KEYS.pedsPercent, current);
}

// ── Kaiju Story Images ─────────────────────────────────────

export function getKaijuStoryImage(kaijuId: KaijuId): string | null {
  const overrides = loadJSON<Record<string, string>>(KEYS.kaijuStoryImages);
  return overrides?.[kaijuId] ?? null;
}

export async function saveKaijuStoryImage(kaijuId: KaijuId, url: string | null): Promise<void> {
  const current = loadJSON<Record<string, string>>(KEYS.kaijuStoryImages) ?? {};
  if (url) {
    current[kaijuId] = url;
  } else {
    delete current[kaijuId];
  }
  await saveSetting(KEYS.kaijuStoryImages, current);
}

// ── Score Rankings ─────────────────────────────────────────

export interface RankingTier {
  title: string;
  minAccuracy: number;
  minSaveRate: number;
}

export interface RankingsConfig {
  tiers: RankingTier[];
  fallback: string;
}

export const DEFAULT_RANKINGS: RankingsConfig = {
  tiers: [
    { title: '🏆 LEGENDARY TRIAGE COMMANDER', minAccuracy: 90, minSaveRate: 80 },
    { title: '⭐ ELITE FIELD MEDIC', minAccuracy: 80, minSaveRate: 70 },
    { title: '🎖️ VETERAN FIRST RESPONDER', minAccuracy: 70, minSaveRate: 60 },
    { title: '💪 FIELD MEDIC — UNDER PRESSURE', minAccuracy: 60, minSaveRate: 50 },
    { title: '🩹 ROOKIE RESPONDER — ROOM TO GROW', minAccuracy: 50, minSaveRate: 0 },
  ],
  fallback: '💀 OVERWHELMED — THE KAIJU WON THIS ROUND',
};

export function getRankings(): RankingsConfig {
  return loadJSON(KEYS.rankings) ?? DEFAULT_RANKINGS;
}

export async function saveRankings(config: RankingsConfig): Promise<void> {
  await saveSetting(KEYS.rankings, config);
}

// ── Outro Story ───────────────────────────────────────────

export function getOutroImages(): (string | null)[] {
  return loadJSON(KEYS.outroImages) ?? [null, null, null, null];
}

export async function saveOutroImages(images: (string | null)[]): Promise<void> {
  await saveSetting(KEYS.outroImages, images);
}

export function getOutroMusic(): string | null {
  return loadJSON(KEYS.outroMusic) ?? null;
}

export async function saveOutroMusic(url: string | null): Promise<void> {
  await saveSetting(KEYS.outroMusic, url);
}

export function getOutroSlideTexts(): string[] {
  return loadJSON(KEYS.outroSlideTexts) ?? ['', '', '', ''];
}

export async function saveOutroSlideTexts(texts: string[]): Promise<void> {
  await saveSetting(KEYS.outroSlideTexts, texts);
}

// ── Reset all overrides ────────────────────────────────────

export async function resetAllOverrides(): Promise<void> {
  Object.values(KEYS).forEach(k => localStorage.removeItem(k));
  clearCache();
  // Also clear from DB
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    for (const key of Object.values(KEYS)) {
      await supabase.from('game_settings').delete().eq('id', key);
    }
  } catch (e) {
    console.warn('Failed to clear DB settings:', e);
  }
}